// SERV-3577 (decision 13, UI73): both serve Edit Reach's save, hidden below.
// import { useQueryClient } from "@tanstack/react-query";
import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
// import { PermissionsQueryKeys } from "@/api/roles-permissions/roles-permissions.queries";
import { ConfirmationModal } from "@/components/ui/ConfirmationModal/ConfirmationModal";
import { EditRoleDetailsModal } from "@/components/features/RolesPermissions/EditRoleDetailsModal/EditRoleDetailsModal";
import { RoleDetailSkeleton } from "@/components/features/RolesPermissions/RoleDetailSkeleton/RoleDetailSkeleton";
import { ReactivateRoleModal } from "@/components/features/RolesPermissions/ReactivateRoleModal/ReactivateRoleModal";
import { RoleAccessTab } from "@/components/features/RolesPermissions/RoleAccessTab/RoleAccessTab";
import { AccessConflictModal } from "@/components/features/RolesPermissions/RoleAccessTab/AccessConflictModal";
import { SaveRoleChangesModal } from "@/components/features/RolesPermissions/RoleAccessTab/SaveRoleChangesModal";
import {
  accessReadOnlyReason,
  discardBody,
  discardTitle,
  UNSAVED_BODY,
  UNSAVED_TITLE,
} from "@/components/features/RolesPermissions/RoleAccessTab/roleAccessCopy";
import { RoleHeader } from "@/components/features/RolesPermissions/RoleHeader/RoleHeader";
import { RoleApprovalsTab } from "@/components/features/RolesPermissions/RoleApprovalsTab/RoleApprovalsTab";
import { RoleScopeTab } from "@/components/features/RolesPermissions/RoleScopeTab/RoleScopeTab";
import { EditScopeModal } from "@/components/features/RolesPermissions/RoleScopeTab/EditScopeModal";
import { RoleUsersTab } from "@/components/features/RolesPermissions/RoleUsersTab/RoleUsersTab";
import { RoleHistoryTab } from "@/components/features/RolesPermissions/RoleHistoryTab/RoleHistoryTab";
import { RemoveFromRoleModal } from "@/components/features/RolesPermissions/RoleUsersTab/RemoveFromRoleModal";
import { SetScopeModal } from "@/components/features/RolesPermissions/SetScopeModal/SetScopeModal";
import { buildScopeQueue } from "@/components/features/RolesPermissions/SetScopeModal/setScopeQueue";
import type { ScopeQueuePerson } from "@/components/features/RolesPermissions/SetScopeModal/setScopeQueue";
import { AddPeopleModal } from "@/components/features/RolesPermissions/AddPeopleModal/AddPeopleModal";
import { useRoleMembership } from "@/hooks/roles-permissions/useRoleMembership";
import { useRoleHolders } from "@/hooks/roles-permissions/useRoleHolders";
// SERV-3577 (decision 13, UI73): Reach is read-only until the engine applies it.
// import { EditReachModal } from "@/components/features/RolesPermissions/RoleScopeTab/EditReachModal";
import { RoleOverviewTab } from "@/components/features/RolesPermissions/RoleOverviewTab/RoleOverviewTab";
import { RolePreviewOverlay } from "@/components/features/RolesPermissions/RolePreviewOverlay";
import { useBypassOr } from "@/hooks/access/useBypassOr";
import { usePermissions } from "@/hooks/auth/usePermissions";
import { useInsideEntity } from "@/hooks/usePlatformView";
import { PLATFORM_TENANT_ID } from "@/store/entityTenantStore";
import { useAccessDraft } from "@/hooks/roles-permissions/useAccessDraft";
import { useDuplicateRole } from "@/hooks/roles-permissions/useDuplicateRole";
import { usePermissionCatalog } from "@/hooks/roles-permissions/usePermissionCatalog";
import { catalogNeverArrived, useRoleHistory } from "@/hooks/roles-permissions/useRoleHistory";
import { countResolvedPermissions } from "@/api/roles-permissions/permissionCatalog";
import { removableCopies } from "@/api/roles-permissions/roleHolders";
import { isRetiredCompanyCopy } from "@/api/roles-permissions/retiredCompanyCopy";
import { useRoleAccessSave } from "@/hooks/roles-permissions/useRoleAccessSave";
import { useSwitchOr } from "@/hooks/access/useSwitchOr";
import { useActivateDeactivateRole } from "@/hooks/roles-permissions/useRolesPermissionsMutations";
import {
  getAllRoles,
  getRoleById,
  useAllModules,
} from "@/hooks/roles-permissions/useRolesPermissionsQueries";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { usePersistedTabs } from "@/hooks/usePersistedTabs";
import { tableSearchSchema } from "@/hooks/useTableUrlPersistence";
import { useRolesStore } from "@/store/rolesStore";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { getRouteIcon } from "@/utils/getRouteIconComponent";
import { showRoleOpenFailedAlert } from "@/utils/roleOpenFailedAlert";
import { Flex } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { LineTab } from "@onengine/component-library";
import { createFileRoute, useBlocker, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { z } from "zod";
import styles from "./roleDetail.module.scss";

const roleDetailSearchSchema = tableSearchSchema.extend({
  tab: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_authenticated/roles-permissions/$roleId")({
  component: RoleDetail,
  validateSearch: roleDetailSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

const ROLE_QUERY_OPTIONS = { retry: false, suppressErrorAlert: true };

const ROLE_TABS = [
  { value: "overview", label: "Overview" },
  { value: "access", label: "Access" },
  { value: "scope", label: "Scope" },
  { value: "approvals", label: "Approvals" },
  { value: "users", label: "Users" },
  { value: "history", label: "History" },
];
const HAS_ACCESS_TAB = ROLE_TABS.some((tab) => tab.value === "access");

function RoleDetail() {
  const { roleId } = useParams({ from: "/_authenticated/roles-permissions/$roleId" });
  const navigate = useNavigate();
  const { hasAccess, isSuperAdmin } = usePermissions();
  const { edit: legacyRoleEdit, create: legacyRoleCreate } = hasAccess({
    module: MODULE_KEYS_MAP.rolesPermissions,
  });
  const { edit: legacyUserEdit } = hasAccess({ module: MODULE_KEYS_MAP.userManagement });
  const canEditRole = useSwitchOr("roles_permissions", "edit", legacyRoleEdit);
  const holdsRoleCreate = useSwitchOr("roles_permissions", "create", legacyRoleCreate);
  // UI12: Add People and Remove write the person (PUT /user-management/users/:id), which asks
  // user_management:edit, not the role's own switch.
  const holdsMembershipWrite = useSwitchOr("user_management", "edit", legacyUserEdit);
  const isInsideEntity = useInsideEntity();
  // Duplicate creates a role, and the role write is taken from super users only.
  const canDuplicate = isSuperAdmin && holdsRoleCreate;
  const duplicateRole = useDuplicateRole();
  // Activate is taken from a super admin or an Entity Admin of the request's company (SERV-3572).
  const canReactivate = useBypassOr(isSuperAdmin);

  const { activeTab: rawActiveTab, setActiveTab } = usePersistedTabs({ defaultTab: "overview" });
  const activeTab = ROLE_TABS.some((tab) => tab.value === rawActiveTab)
    ? rawActiveTab
    : ROLE_TABS[0].value;

  const { data: roleResponse, isPending: isRoleLoading } = getRoleById(
    roleId,
    true,
    ROLE_QUERY_OPTIONS,
  );
  const {
    data: modulesResponse,
    isLoading: isModulesLoading,
    isError: isModulesError,
    refetch: refetchModules,
  } = useAllModules();
  const role = roleResponse?.data;
  const modules = useMemo(() => modulesResponse?.data ?? [], [modulesResponse?.data]);

  const { activateRoleMutation, isActivatingRole } = useActivateDeactivateRole();
  const [isEditDetailsOpen, editDetailsHandlers] = useDisclosure(false);
  const [isReactivateOpen, reactivateHandlers] = useDisclosure(false);
  // SERV-3577 (decision 13, UI73): restore with Edit Reach.
  // const [isEditReachOpen, editReachHandlers] = useDisclosure(false);
  // const queryClient = useQueryClient();
  const [isEditScopeOpen, editScopeHandlers] = useDisclosure(false);
  const [isPreviewOpen, previewHandlers] = useDisclosure(false);
  const [isDiscardOpen, discardHandlers] = useDisclosure(false);

  const isAccessTab = activeTab === "access";
  const isApprovalsTab = activeTab === "approvals";
  const isScopeTab = activeTab === "scope";
  const isUsersTab = activeTab === "users";
  const isHistoryTab = activeTab === "history";
  const {
    catalog,
    isComplete: isCatalogReady,
    isLoading: isCatalogLoading,
    isError: isCatalogError,
    refetch: refetchCatalog,
    // One request, cached for the session, so both tabs can rely on it.
  } = usePermissionCatalog(modules);

  // A deactivated role grants nothing and cannot be given out, so its access is read-only
  // until it is reactivated (frame `edge-role-inactive`).
  const canEdit = canEditRole && role?.status !== "inactive";
  // The role write applies to every company's copy, so the backend takes it from super users only.
  const canSaveRole = isSuperAdmin && canEdit;
  // D251: the platform company's copy (Team Lead, Entity Lead, Admin Coordinator: Manager,
  // Oversight) is given and taken by super admins only; the backend refuses it below that.
  const canChangeMembers =
    holdsMembershipWrite &&
    role?.status !== "inactive" &&
    (isSuperAdmin || role?.tenantId !== PLATFORM_TENANT_ID);
  const holdsScopeWrite = useSwitchOr("user_management", "edit", true);
  // Set Scope is a scope write (user_management), not a change to the role, so it keeps the
  // legacy grant rather than following roles_permissions edit.
  const canSetScope = legacyRoleEdit && role?.status !== "inactive" && holdsScopeWrite;

  // The Copy From Role picker needs the other roles; one page is plenty for a dropdown.
  const { data: rolesList } = getAllRoles({ page: "1", pageSize: "100" });
  const rolesForCopy = rolesList?.data ?? [];

  // The holder list feeds both the Users tab and Add People, which sits in the header on every tab.
  const thisRole = useMemo(
    () =>
      role
        ? { id: role.id, code: role.code ?? null, name: role.name, tenantId: role.tenantId ?? null }
        : null,
    [role],
  );
  const holders = useRoleHolders(thisRole);
  const catalogMissing = catalogNeverArrived({
    isCatalogError,
    isModulesError,
    isCatalogLoading,
    isCatalogReady,
  });
  const history = useRoleHistory(roleId, catalog, isCatalogReady, catalogMissing, isHistoryTab);
  const membership = useRoleMembership(roleId);
  const holderIds = useMemo(() => holders.rows.map((row) => row.userId), [holders.rows]);
  const [isAddPeopleOpen, addPeopleHandlers] = useDisclosure(false);
  // The id, not the row: the holders read refetches after a membership change, and a snapshot taken
  // at click would keep showing the roles the person held before it.
  const [removeUserId, setRemoveUserId] = useState<string | null>(null);
  const personToRemove = holders.rows.find((row) => row.userId === removeUserId) ?? null;

  // The queue is frozen when the modal opens. The holders query refetches after every save, so
  // a live queue would drop the person being looked at the moment their scope landed and
  // renumber every step behind them.
  const [scopeQueue, setScopeQueue] = useState<ScopeQueuePerson[] | null>(null);
  const [scopeStartAt, setScopeStartAt] = useState<string | undefined>(undefined);
  const openSetScope = (startAtUserId?: string) => {
    setScopeQueue(buildScopeQueue(holders.rows));
    setScopeStartAt(startAtUserId);
  };

  const draft = useAccessDraft(role, catalog);
  const access = useRoleAccessSave(
    role,
    draft.baseline,
    { actionIds: draft.effectiveActionIds, moduleIds: draft.activeModuleIds },
    catalog,
  );

  const isDirty = isAccessTab && draft.isDirty;

  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  const blocker = useBlocker({
    shouldBlockFn: () => isDirtyRef.current,
    withResolver: true,
    enableBeforeUnload: () => isDirtyRef.current,
  });

  const [pendingTab, setPendingTab] = useState<string | null>(null);

  const roleName = role?.name;
  const breadcrumbItems = useMemo(
    () => [
      {
        title: "Roles & Permissions",
        href: "/roles-permissions",
        icon: getRouteIcon(MODULE_KEYS_MAP.rolesPermissions, 16),
      },
      {
        title: roleName ?? (isRoleLoading ? "Loading..." : "Role not found"),
        href: `/roles-permissions/${roleId}`,
      },
    ],
    [roleName, isRoleLoading, roleId],
  );
  useBreadcrumb(breadcrumbItems, { listView: "List View" });

  const failedToOpen = !isRoleLoading && !role;
  const redirectedRoleId = useRef<string | null>(null);
  useEffect(() => {
    if (!failedToOpen || redirectedRoleId.current === roleId) return;
    redirectedRoleId.current = roleId;
    navigate({ to: "/roles-permissions", replace: true });
    showRoleOpenFailedAlert({
      roleId,
      onRetry: () => navigate({ to: "/roles-permissions/$roleId", params: { roleId } }),
    });
  }, [failedToOpen, roleId, navigate]);

  const getRovingTabIndexProps = (tabValue: string) => ({
    tabIndex: activeTab === tabValue ? 0 : -1,
  });

  const requestTab = (tabValue: string) => {
    if (tabValue === activeTab) return;
    if (isDirtyRef.current) {
      setPendingTab(tabValue);
      return;
    }
    setActiveTab(tabValue);
  };

  const handleTabKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const currentIndex = ROLE_TABS.findIndex((tab) => tab.value === activeTab);
    const targetIndexByKey: Partial<Record<string, number>> = {
      ArrowRight: (currentIndex + 1) % ROLE_TABS.length,
      ArrowLeft: (currentIndex - 1 + ROLE_TABS.length) % ROLE_TABS.length,
      Home: 0,
      End: ROLE_TABS.length - 1,
    };
    const targetIndex = targetIndexByKey[event.key];
    if (targetIndex === undefined) return;
    event.preventDefault();
    if (targetIndex === currentIndex) return;
    const nextTab = ROLE_TABS[targetIndex];
    requestTab(nextTab.value);
    document.getElementById(`role-tab-${nextTab.value}`)?.focus();
  };

  if (isRoleLoading || !role) {
    return <RoleDetailSkeleton />;
  }

  // Decision 5 (UI13): at the main domain an add acts in the platform company, whose copy of an
  // entity-level role grants nothing at any login (D10).
  const canAddPeople = canChangeMembers && (isInsideEntity || role.level !== "entity");

  const handleOpenAccess = () => {
    if (HAS_ACCESS_TAB) {
      setActiveTab("access");
      return;
    }
    useRolesStore.getState().setRole({
      name: role.name,
      description: role.description,
      level: role.level ?? "",
      department: role.department ?? "",
      roleId: role.id,
    });
    useRolesStore.getState().setRoleId(role.id);
    navigate({ to: "/roles-permissions/assign-permissions" });
  };

  const handleReactivateConfirm = () => {
    activateRoleMutation.mutate(role.id, { onSettled: reactivateHandlers.close });
  };

  const confirmLeaveTab = () => {
    if (!pendingTab) return;
    draft.discard();
    // Changing the tab is a navigation, and the blocker reads this ref synchronously — before
    // the discard has re-rendered — so it must be cleared here or the guard asks a second time.
    isDirtyRef.current = false;
    setActiveTab(pendingTab);
    setPendingTab(null);
  };

  return (
    <>
      <Flex
        direction="column"
        className={`${styles.container} ${isAccessTab ? styles.containerFill : ""}`}
      >
        <RoleHeader
          name={role.name}
          status={role.status}
          level={role.level}
          canDuplicate={canDuplicate}
          canAddPeople={canAddPeople}
          canReactivate={canReactivate && !isRetiredCompanyCopy(role)}
          onDuplicate={() => duplicateRole(role)}
          onAddPeople={() => {
            membership.clearFailures();
            addPeopleHandlers.open();
          }}
          onReactivate={reactivateHandlers.open}
          isDirty={isDirty}
          isSaving={access.isSaving}
          onDiscard={discardHandlers.open}
          onSave={access.openSave}
          canSave={isCatalogReady}
        />

        <div
          role="tablist"
          aria-label="Role sections"
          className={styles.tabRow}
          onKeyDown={handleTabKeyDown}
        >
          {ROLE_TABS.map((tab) => (
            <LineTab
              key={tab.value}
              role="tab"
              size="lg"
              className={styles.lineTab}
              selected={activeTab === tab.value}
              aria-selected={activeTab === tab.value}
              id={`role-tab-${tab.value}`}
              ariaControls={`role-panel-${tab.value}`}
              label={
                tab.value === "users" && holders.peopleCount !== null
                  ? `${tab.label} (${holders.peopleCount})`
                  : tab.label
              }
              {...getRovingTabIndexProps(tab.value)}
              onActivate={() => requestTab(tab.value)}
            />
          ))}
        </div>

        <div
          role="tabpanel"
          id={`role-panel-${activeTab}`}
          aria-labelledby={`role-tab-${activeTab}`}
          className={`${styles.panel} ${isAccessTab ? styles.panelFill : ""}`}
        >
          {isAccessTab ? (
            <RoleAccessTab
              modules={modules}
              isModulesLoading={isModulesLoading}
              isModulesError={isModulesError}
              onRetryModules={() => void refetchModules()}
              catalog={catalog}
              isCatalogLoading={isCatalogLoading}
              isCatalogReady={isCatalogReady}
              isCatalogError={isCatalogError}
              onRetryCatalog={refetchCatalog}
              draft={draft}
              canEdit={canSaveRole}
              readOnlyReason={accessReadOnlyReason({
                canSave: canSaveRole,
                isInactive: role.status === "inactive",
              })}
              onPreview={previewHandlers.open}
              roleId={role.id}
              roleName={role.name}
              roles={rolesForCopy}
            />
          ) : isUsersTab ? (
            <RoleUsersTab
              roleName={role.name}
              roleTenantId={role.tenantId ?? null}
              canEdit={canChangeMembers}
              holders={holders}
              onRemove={(person) => {
                membership.clearRemoveFailure();
                setRemoveUserId(person.userId);
              }}
              canSetScope={canSetScope}
              onSetScope={openSetScope}
            />
          ) : isHistoryTab ? (
            <RoleHistoryTab
              roleName={role.name}
              history={history}
              namesUnavailable={catalogMissing && !isCatalogReady}
            />
          ) : isScopeTab ? (
            <RoleScopeTab role={role} canEdit={canSaveRole} onEditScope={editScopeHandlers.open} />
          ) : isApprovalsTab ? (
            <RoleApprovalsTab
              permissionIds={role.permissionIds}
              moduleIds={role.moduleIds}
              catalog={catalog}
              isCatalogLoading={isCatalogLoading}
              isCatalogReady={isCatalogReady}
              isCatalogError={isCatalogError}
              isModulesError={isModulesError}
              onRetryCatalog={refetchCatalog}
              onRetryModules={() => void refetchModules()}
            />
          ) : (
            <RoleOverviewTab
              role={role}
              modulesTotal={modules.length}
              permissionsOn={countResolvedPermissions(role.permissionIds, catalog)}
              isCatalogReady={isCatalogReady}
              canEdit={canSaveRole}
              hasAccessTab={HAS_ACCESS_TAB}
              onEditDetails={editDetailsHandlers.open}
              onOpenAccess={handleOpenAccess}
            />
          )}
        </div>
      </Flex>

      {/* SERV-3577 (decision 13, UI73): Reach is read-only until the engine applies it; the
          modal keeps its SERV-3572 conflict handling for when Edit comes back.
      <EditReachModal
        isOpen={isEditReachOpen}
        onClose={editReachHandlers.close}
        role={role}
        onSaved={() =>
          void queryClient.invalidateQueries({
            queryKey: [PermissionsQueryKeys.getRoleById, role.id],
          })
        }
      />
      */}

      <EditScopeModal isOpen={isEditScopeOpen} onClose={editScopeHandlers.close} role={role} />

      <AddPeopleModal
        isOpen={isAddPeopleOpen}
        roleName={role.name}
        givesServEngineAccess={role.tenantId === PLATFORM_TENANT_ID}
        currentHolderIds={holderIds}
        holdersKnown={!holders.isLoading && !holders.isError}
        isAdding={membership.isAdding}
        lastAdd={membership.lastAdd}
        onClose={() => {
          membership.clearFailures();
          addPeopleHandlers.close();
        }}
        onAdd={(people) =>
          membership.addPeople(people, {
            onSuccess: (outcome) => {
              // The modal stays open while anyone is still missing, so the names in its Alert
              // are still picked and can be tried again without being typed in a second time.
              if (outcome.failed.length === 0) {
                addPeopleHandlers.close();
                setActiveTab("users");
              }
            },
          })
        }
      />

      <RemoveFromRoleModal
        person={personToRemove}
        roleName={role.name}
        isRemoving={membership.isRemoving}
        hasFailed={membership.removeFailed}
        onClose={() => {
          membership.clearRemoveFailure();
          setRemoveUserId(null);
        }}
        onConfirm={() => {
          if (!personToRemove || !thisRole) return;
          membership.removePerson(
            {
              userId: personToRemove.userId,
              copies: removableCopies(personToRemove.entities, thisRole),
            },
            { onSuccess: () => setRemoveUserId(null) },
          );
        }}
      />

      <SetScopeModal
        isOpen={scopeQueue !== null}
        roleName={role.name}
        queue={scopeQueue ?? []}
        startAtUserId={scopeStartAt}
        onClose={() => {
          setScopeQueue(null);
          setScopeStartAt(undefined);
        }}
        onSaved={() => holders.refetch()}
      />

      <EditRoleDetailsModal
        isOpen={isEditDetailsOpen}
        onClose={editDetailsHandlers.close}
        role={role}
      />

      <ReactivateRoleModal
        open={isReactivateOpen}
        isReactivating={isActivatingRole}
        onClose={reactivateHandlers.close}
        onConfirm={handleReactivateConfirm}
      />

      <RolePreviewOverlay
        isOpen={isPreviewOpen}
        onClose={previewHandlers.close}
        roleName={role.name}
        roleDescription={role.description}
        activeModules={modules.filter((module) => draft.activeModuleIds.includes(module.id))}
        checkedActionIds={draft.effectiveActionIds}
      />

      <SaveRoleChangesModal
        isOpen={access.isSaveOpen}
        role={role}
        changes={draft.changes}
        isSaving={access.isSaving}
        hasFailed={access.hasFailed}
        failureMessage={access.failureMessage}
        onClose={access.closeSave}
        onConfirm={access.confirmSave}
      />

      {access.conflict && (
        <AccessConflictModal
          isOpen
          roleName={role.name}
          savedBy={access.conflict.savedBy}
          savedAt={access.conflict.savedAt}
          theirChanges={access.conflict.theirChanges}
          myChanges={draft.changes}
          isSaving={access.isSaving}
          onKeepTheirs={access.keepTheirVersion}
          onDismiss={access.dismissConflict}
          onSaveMine={access.saveMyChanges}
        />
      )}

      <ConfirmationModal
        isOpen={isDiscardOpen}
        onClose={discardHandlers.close}
        title={discardTitle()}
        body={discardBody(draft.changes.length, role.name)}
        confirmText="Discard"
        confirmVariant="destructive-primary"
        onConfirm={() => {
          draft.discard();
          discardHandlers.close();
        }}
      />

      <ConfirmationModal
        isOpen={pendingTab !== null}
        onClose={() => setPendingTab(null)}
        title={UNSAVED_TITLE}
        body={UNSAVED_BODY}
        cancelText="Stay"
        confirmText="Leave"
        confirmVariant="destructive-primary"
        onConfirm={confirmLeaveTab}
      />

      {blocker.status === "blocked" && (
        <ConfirmationModal
          isOpen
          onClose={() => blocker.reset()}
          title={UNSAVED_TITLE}
          body={UNSAVED_BODY}
          cancelText="Stay"
          confirmText="Leave"
          confirmVariant="destructive-primary"
          onConfirm={() => blocker.proceed()}
        />
      )}
    </>
  );
}
